import { useState } from "react";
import { useHistory } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { fileToBase64EncodedImage } from "@/utilities/fileToBase64EncodedImage";
import {
  PropertyManagementListSnackbarState,
  SelectedPropertyId,
  SelectedPropertyIds,
  useRequestInitialLoading,
} from "@/view_models/atoms";
import { SelectedProperty } from "@/view_models/propertySelectors";

type DropDownMenuItem = { label: string; onClick: () => void; color: "textPrimary" | "error" };

// 物件詳細画面の物件更新系アクションを管理するフック
export const useManagementDetailActions = () => {
  const history = useHistory();
  const selected_property = useRecoilValue(SelectedProperty);

  const { updatePropertyImage } = useUpdatePropertyImage();
  const { deleteProperty } = useDeleteProperty();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDeletePropertyModal, setOpenDeletePropertyModal] = useState(false);
  const [openChangeImagePropertyModal, setOpenChangeImagePropertyModal] = useState(false);

  const openDeleteModal = () => setOpenDeletePropertyModal(true);
  const closeDeleteModal = () => setOpenDeletePropertyModal(false);
  const openImageModal = () => setOpenChangeImagePropertyModal(true);
  const closeImageModal = () => setOpenChangeImagePropertyModal(false);
  const openMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  // 物件削除のハンドラー
  const handleDeleteProperty = async (propertyId: number) => {
    await deleteProperty(propertyId);
    closeDeleteModal();
    if (selected_property.property.property_type === PROPERTY_TYPE_VALUES.consideration) {
      history.push("/management/consideration");
    } else {
      history.push("/management/own");
    }
  };

  // 物件画像更新のハンドラー
  const handleUpdateImage = async (propertyId: number, imageFile: File) => {
    await updatePropertyImage(propertyId, imageFile);
    closeImageModal();
    if (selected_property.property.property_type === PROPERTY_TYPE_VALUES.consideration) {
      history.push("/management/consideration");
    } else {
      history.push("/management/own");
    }
  };

  // ドロップダウンメニューの項目
  const menuItems: DropDownMenuItem[] = [
    { label: "画像を変更する", onClick: () => setOpenChangeImagePropertyModal(true), color: "textPrimary" },
    { label: "物件を削除する", onClick: () => setOpenDeletePropertyModal(true), color: "error" },
    // 2024/08/02: 一時的に非表示とする
    // クリック時UpdatePropertyTypeToOwnModalを表示する。
    // この機能を戻す時は処理を揃えるために、useDeleteProperty, DeletePropertyModalを参考に修正した上で使用すること。
    // { label: "保有物件に移動する", onClick: handlePropertyTypeUpdate, color: "textPrimary" },
  ];

  return {
    anchorEl,
    openDeletePropertyModal,
    openChangeImagePropertyModal,
    openDeleteModal,
    closeDeleteModal,
    openImageModal,
    closeImageModal,
    openMenu,
    closeMenu,
    handleDeleteProperty,
    handleUpdateImage,
    menuItems,
  };
};

// 物件削除用のフック
// 他の場所で使用する場合、必要に応じてファイル分割しても良い
export const useDeleteProperty = () => {
  const fp = PropallyApiFactory();

  const requestInitialLoading = useRequestInitialLoading();
  const setManagementListSnackBarState = useSetRecoilState(PropertyManagementListSnackbarState);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);

  const deleteProperty = async (propertyId: number) => {
    try {
      await fp.v1PropertiesPropertyIdDelete(propertyId, {
        withCredentials: true,
      });

      // 物件の削除に成功した場合、選択中の物件IDをリセット
      setSelectedPropertyIds([]);
      setSelectedPropertyId(null);

      // 画像の登録に成功したら、物件情報を再取得する
      requestInitialLoading();

      setManagementListSnackBarState({ isOpen: true, message: "物件を削除しました", status: "success" });
    } catch (error) {
      setManagementListSnackBarState({ isOpen: true, message: "物件を削除できませんでした", status: "error" });
    }
  };

  return {
    deleteProperty,
  };
};

// 物件画像更新用のフック
// 他の場所で使用する場合、必要に応じてファイル分割しても良い
const useUpdatePropertyImage = () => {
  const fp = PropallyApiFactory();

  const requestInitialLoading = useRequestInitialLoading();
  const setManagementListSnackBarState = useSetRecoilState(PropertyManagementListSnackbarState);

  const updatePropertyImage = async (propertyId: number, imageFile: File) => {
    const base64_encoded_image = await fileToBase64EncodedImage(imageFile);

    if (!base64_encoded_image) return;

    try {
      const result = await fp.v1PropertiesPropertyIdImagesPost(propertyId, base64_encoded_image, {
        withCredentials: true,
      });

      if (result.status === 201) {
        requestInitialLoading();
        setManagementListSnackBarState({ isOpen: true, message: "画像を変更しました", status: "success" });
        return;
      }

      setManagementListSnackBarState({ isOpen: true, message: "画像を変更できませんでした", status: "error" });
    } catch (error) {
      setManagementListSnackBarState({ isOpen: true, message: "画像を変更できませんでした", status: "error" });
    }
  };

  return {
    updatePropertyImage,
  };
};
